import { Controller } from "@hotwired/stimulus";
import { FabricEditor, DropHandler, LAYER_PROPS, canvasImageSource, isCropped } from "@frabr/fabric-editor";
import {
  attachBoundTextEditing,
  bindSelectedField,
  unbindSelectedField,
  setSelectedText,
  insertTokenAtCaret,
  interpolateBoundFields,
  withExpressions,
} from "../../lib/fabric_bindings";
import { interpolateLayers } from "../../lib/variable_interpolation";
import { FabricControls } from "../../lib/fabric_controls";
import { FabricLayout } from "../../lib/fabric_layout";
import { FabricAppearance } from "../../lib/fabric_appearance";
import { ToolboxDropZone } from "../../lib/toolbox_drop_zone";
import { armToolboxDrag, canvasPlacement, placeToolboxItem, dropToolboxItem } from "../../lib/toolbox_dispatch";
import { uploadToLibrary } from "../../lib/media_library";
import { isBlankSegment, mediumMeta } from "../../lib/composition_document";
import { BG_LAYER_ID, imageBackgroundLayer } from "../../lib/fabric_background";
import { segmentCropOf, cropFromFrame } from "../../lib/segment_crop";
import { workspace, followFrame, nearFrameEdge, pageName, themeColor } from "../../lib/editor_stage";
import { hasResolvableLegacyClip, streamShapeCatalogs } from "../../lib/shape_catalogs";
import { computeCtxState } from "../../lib/editor_tracks.ts";
import {
  nestedStates,
  DEFAULT_WIDTH,
  DEFAULT_HEIGHT,
  totalOutputDuration,
  segmentAt,
  segmentEdgeTimeById,
  pruneOrphanOverlays,
  segmentId,
} from "../../lib/composition_v3";

/**
 * Manages the fabric.js canvas overlay for visual editing of composition overlays.
 *
 * Handles:
 *  - canvas init, resize, dispose
 *  - adding shapes, text, images
 *  - syncing fabric objects ↔ overlay position data
 *  - visibility based on playhead time
 *  - floating toolbar actions (fc* methods)
 *
 * Communicates with the main composition-editor controller via outlet.
 * Naming: "composition-editor--fabric" in HTML.
 */
export default class extends Controller {
  static outlets = ["composition-editor"];

  static targets = [
    "canvas",
    "wrap",
    "layerControls",
    // Plan de travail : la couche calée sous le cadre (l'iframe de preview)
    "frame",
    "frameCue",
    // La séquence dans le cadre : son nom, l'invitation à poser un fond (fabric/_page_overlay)
    "pageOverlay",
    "pageName",
    "pageEmpty",
  ];

  static values = {
    fonts: { type: Object, default: {} },
    // Les URLs des catalogues de formes (global + chartes, lib/shape_catalogs.ts) — la lib
    // n'embarque aucune forme, la résolution des clipShape du stock en dépend.
    shapeCatalogs: { type: Array, default: [] },
    // Les valeurs du lecteur courant : les habillages liés s'affichent interpolés
    // (canvas et preview), le document garde ses tokens.
    variables: { type: Object, default: {} },
    // La médiathèque du lecteur, où entre un fichier lâché sur le canvas.
    uploadEndpoint: { type: String, default: "/toolbox/content_media" },
  };

  // ==================== Lifecycle ====================

  connect() {
    // Defer init until parent outlet is connected
    this._initPending = true;
    this._tryInit();

    // Toolbox — arm the external drag when a toolbox item starts dragging.
    // Document-level: this controller is mounted on the preview area only,
    // and the toolbox drawer is a sibling — the event never bubbles here.
    this._boundOnToolboxDragStart = (e) => {
      const data = e.detail;
      if (!data?.type) return;
      // Les vidéos ne se déposent que sur la timeline — ou sur une séquence vide, qui en
      // fait son fond : on n'arme pas le drop canvas.
      if (data.type === "media" && data.media_type === "video") {
        if (this._pageEmptyShown && this.hasWrapTarget) this.wrapTarget.style.pointerEvents = "auto";
        return;
      }
      // Let the fabric wrap intercept drags above the iframe.
      // (The ToolboxDropZone onDragStart can't do it: at document capture
      // time the payload isn't set yet, so its MIME check never passes.)
      if (this.hasWrapTarget) this.wrapTarget.style.pointerEvents = "auto";
      if (this._imageDropHandler) armToolboxDrag(this._toolboxHost, data);
    };
    document.addEventListener("toolbox-item:dragstart", this._boundOnToolboxDragStart);

    // Canvas drop zone (delegates to DropHandler API).
    // Global drag start/end activate pointer-events on the fabric wrap
    // so it intercepts drags above the iframe.
    this._toolboxDrop = new ToolboxDropZone(this.element, {
      onDragOver: (e) => {
        this._imageDropHandler?.trackPointer(e);
        this._toggleEdgeCue(this._nearFrameEdge(e));
      },
      onDrop: async (e, data) => {
        this._toggleEdgeCue(false);
        // Au bord du cadre (ou n'importe où sur une séquence vide), le média devient le fond
        if (this._takeMediaAsBackground(e, data)) return;
        if (this._imageDropHandler) await dropToolboxItem(this._toolboxHost, e, data);
      },
      onLeave: () => {
        this._toggleEdgeCue(false);
        this._imageDropHandler?.suspendDrag();
      },
      onDragEnd: () => {
        this._toggleEdgeCue(false);
        if (this.hasWrapTarget) this.wrapTarget.style.pointerEvents = "";
        this._imageDropHandler?.cancelDrag();
      },
      ghostZone: this.hasWrapTarget ? this.wrapTarget : this.element,
    });
  }

  compositionEditorOutletConnected() {
    if (this._initPending) this._tryInit();
  }

  _tryInit() {
    if (!this._editor || !this.hasCanvasTarget) return;
    this._initPending = false;
    this._initCanvas();
  }

  disconnect() {
    document.removeEventListener("toolbox-item:dragstart", this._boundOnToolboxDragStart);
    this._toolboxDrop?.destroy();
    this._fabricLayout?.destroy();
    this._fabricAppearance?.destroy();
    this._detachBoundTextEditing?.();
    this._detachBoundTextEditing = null;
    this._imageDropHandler = null;
    this._controls?.destroy();
    this._fabricEditor?.dispose();
    this._fabricLayout = null;
    this._fabricAppearance = null;
    this._controls = null;
    this._fabricEditor = null;
  }

  // ==================== Public API (called by parent) ====================

  /** The underlying FabricEditor instance. */
  get editor() { return this._fabricEditor; }

  /** True when the user is editing text inside a fabric IText object. */
  isEditingText() {
    // isEditing only exists on text objects (i-text, textbox) — don't
    // match on type, CustomTextbox reports "textbox"
    return !!this._fabricEditor?.canvas.getActiveObject()?.isEditing;
  }

  /**
   * Show/hide individual fabric objects based on whether the playhead
   * time falls within their overlay's [start, end] range.
   */
  syncVisibility() {
    if (!this._fabricEditor) return;
    const t = this._editor.playTime;
    const overlays = this._editor.composition.overlays || [];
    // Un conteneur masqué emporte ses enfants, comme au rendu (FabricTree).
    const states = nestedStates(overlays);
    let changed = false;

    this._fabricEditor.canvas.getObjects().forEach((obj) => {
      const id = obj.get("layerId");
      if (!id) return;
      const idx = overlays.findIndex((o) => o._fabricId === id);
      if (idx === -1) return;

      const overlay = overlays[idx];
      const shouldBeVisible = !states[idx].hidden && t >= (overlay.start ?? 0) && t < (overlay.end ?? Infinity);
      if (obj.visible !== shouldBeVisible) {
        obj.visible = shouldBeVisible;
        changed = true;
      }
    });

    if (changed) this._fabricEditor.canvas.renderAll();
  }

  /**
   * Enable or disable fabric canvas interactivity.
   * Called when toggling play/pause.
   */
  /**
   * Le canvas d'édition s'efface pour le lecteur (lecture) ou revient. La barre d'actions,
   * hors du canvas (pour ne pas être rognée), se cache en CSS sur `data-canvas-off` de la
   * scène : elle garde son état et revient d'elle-même. `keepSelection` (la lecture de plage,
   * l'aperçu d'une animation) : le bloc reste sélectionné, la toolbox reste sur sa page.
   */
  setEditable(editable, { keepSelection = false } = {}) {
    if (!this.hasWrapTarget) return;
    this.wrapTarget.style.pointerEvents = editable ? "auto" : "none";
    this.wrapTarget.style.opacity = editable ? "1" : "0";
    this.element.toggleAttribute("data-canvas-off", !editable);
    if (this._fabricEditor) {
      if (!keepSelection) this._fabricEditor.setInteractive(editable);
      if (editable) this.syncVisibility();
    }
  }

  /**
   * Restore fabric objects from composition overlays that have _fabricLayers.
   * Called on init and after undo/redo restore.
   */
  async restoreOverlays() {
    if (!this._fabricEditor) return;

    const allLayers = (this._editor.composition.overlays || [])
      .filter((o) => o._fabricId && o._fabricLayers?.length)
      .flatMap((o) => o._fabricLayers);

    await this._fabricEditor.replaceAllLayers(allLayers);
    // Le document porte des tokens : chaque reconstruction du canvas ré-interpole pour CE
    // lecteur — comme le montage de page de l'éditeur images.
    interpolateBoundFields(this._fabricEditor, this.variablesValue);

    // Hors lecture, le canvas est l'éditeur : il reçoit les clics (un clic à vide sélectionne
    // la séquence) même sans habillage
    if (!this._editor?._playing) this.setEditable(true);
  }

  /** Les overlays prêts pour un rendu d'affichage (preview Remotion) : les habillages liés
   *  partent interpolés pour CE lecteur — copies, le document n'est jamais touché. */
  interpolatedOverlays(overlays) {
    return (overlays || []).map((overlay) =>
      overlay._fabricLayers?.length
        ? { ...overlay,
            _fabricLayers: interpolateLayers(overlay._fabricLayers, this.variablesValue, { keepUnknown: true }) }
        : overlay,
    );
  }

  // ==================== La séquence (la page de la vidéo) ====================
  // Elle se sélectionne d'un clic à vide dans le cadre, sur son nom, ou dans la timeline
  // (l'orchestrateur) : la barre de son fond, la page Fond (FabricControls).

  /** Le nom de la séquence : un clic la sélectionne — ou la désélectionne. */
  selectPage() {
    this._controls?.togglePage();
  }

  /** L'invitation d'une séquence vide : sur l'onglet Couleur de la page Fond. */
  chooseBackgroundColor() {
    this._controls?.selectPage("color");
  }

  /** L'orchestrateur a sélectionné une séquence (timeline) : c'est la page — et `panel`
   *  (`video`, `image`, `color`) ouvre cette page de son fond dans la toolbox. */
  showSegmentPage(index, panel) {
    if (this._controls?.isPageSelected && this._pageIndex === index && !panel) return;
    this._pageIndex = index;
    this._identifyPageSegment();
    this._pageFromTimeline = true;
    this._controls?.selectPage(panel);
    this._pageFromTimeline = false;
  }

  deselectPage() {
    this._controls?.deselectPage();
  }

  /** Le nom et l'invitation suivent la séquence sous la tête de lecture ; la barre, son fond. */
  refreshPage() {
    const editor = this._editor;
    if (!editor?.composition) return;

    const segments = editor.composition.segments || [];
    const seg = this._controls?.isPageSelected ? this._pageSegment() : segmentAt(editor.composition, editor.playTime);
    const index = segments.indexOf(seg);
    if (this.hasPageNameTarget) {
      const { width, height } = editor.composition;
      this.pageNameTarget.textContent =
        pageName(this.pageNameTarget.dataset.nameTemplate || "%{number}", index + 1, width, height, seg?.label);
      this.pageNameTarget.hidden = !seg;
    }
    this._pageEmptyShown = Boolean(seg && isBlankSegment(seg) && !editor._playing);
    if (this.hasPageEmptyTarget) this.pageEmptyTarget.hidden = !this._pageEmptyShown;
    this._controls?.refreshPage();
  }

  /** La séquence sélectionnée comme page. */
  _pageSegment() {
    return this._editor?.composition.segments?.[this._pageIndex] || null;
  }

  /** La page sélectionnée sur le canvas (clic à vide, nom) : la séquence sous la tête de
   *  lecture, que l'orchestrateur sélectionne à son tour. */
  _onPageSelect() {
    if (this._pageFromTimeline || !this._editor) return;

    const { composition, playTime } = this._editor;
    this._pageIndex = (composition.segments || []).indexOf(segmentAt(composition, playTime));
    if (this._pageIndex === -1) this._pageIndex = 0;
    this._identifyPageSegment();
    this._editor.select("segment", this._pageIndex);
  }

  /** Les pages du fond désignent la séquence par son id : le bouche-trou, dérivé, n'en a
   *  pas — il en reçoit un, et le document le garde. */
  _identifyPageSegment() {
    const seg = this._pageSegment();
    if (!seg || seg.id) return;
    seg.id = segmentId();
    this._editor.mutate();
  }

  /** La barre de la séquence : son fond — sa couleur (la pastille, sans média), l'outil du
   *  média en cours, qui devient « Remplacer… », et, sur une image, son recadrage et son
   *  mouvement (Remotion les rend, cf. creatorstudio DynamicVideoV2) —, puis la séquence
   *  elle-même : relancer un plan IA échoué, sa vitesse (une vidéo lisible), sa répétition
   *  (stade template : la barre ne l'offre qu'alors), la dupliquer, la supprimer. */
  _pageBar() {
    const seg = this._pageSegment();
    const hasMedia = Boolean(seg?.src?.url || seg?.src?.fallback_url);
    const tools = ["backgroundColor", "backgroundImage", "backgroundVideo"];
    if (hasMedia && seg.type === "image") tools.push("backgroundCrop", "backgroundMotion");
    const sequence = computeCtxState(seg);
    if (sequence.showRetry) tools.push("sequenceRetry");
    if (sequence.showSpeed) tools.push("sequenceSpeed");
    if (seg) tools.push("sequenceRepeat", "sequenceDuplicate", "sequenceDelete");
    return {
      tools,
      state: {
        color: hasMedia ? null : seg?.backgroundColor || "#000000",
        backgroundKind: hasMedia ? seg.type : "color",
        speed: seg?.speed || 1,
        repeat: Boolean(seg?.repeat),
      },
    };
  }

  /**
   * Recadrer l'image de fond de la séquence. Le fond n'est pas sur le canvas (Remotion le
   * rend sous lui) : le temps du geste, un ImageFrame `bg` plein cadre, hors document, porte
   * l'image avec son cadrage — le mode recadrage de la lib s'y applique comme dans l'éditeur
   * images. À la sortie, le cadrage repart sur le segment et le cadre disparaît.
   */
  async _cropSegmentImage() {
    const seg = this._pageSegment();
    const url = seg?.type === "image" ? seg.src?.url || seg.src?.fallback_url : null;
    if (!url || !this._fabricEditor || !this._controls) return;

    const { width, height } = this._fabricEditor;
    const layer = imageBackgroundLayer(width, height, url);
    Object.assign(layer.image, segmentCropOf(seg));
    const [frame] = await this._fabricEditor.layers.deserializeAll([layer]);
    if (!frame) return;

    const canvas = this._fabricEditor.canvas;
    const onEnd = (e) => {
      if (e.target !== frame) return;
      canvas.off("crop:end", onEnd);
      canvas.originalFabricCanvas.remove(frame);
      canvas.requestRenderAll();
      this._backgroundFrame = null;
      if (!e.changed) return;
      if (isCropped(frame.crop)) seg.crop = cropFromFrame(frame);
      else delete seg.crop;
      this._editor.mutate();
    };
    canvas.on("crop:end", onEnd);
    // Le temps du geste, le canvas porte un objet hors document : ses « modifications »
    // (FabricControls#onCropEnd) ne sont pas celles du document — c'est onEnd qui mute, une fois
    this._backgroundFrame = frame;
    canvas.originalFabricCanvas.insertAt(0, frame);
    this._controls.cropBackground();
  }

  /** Un média lâché dans la bande du fond (tout le cadre d'une séquence vide) : il en devient
   *  le fond. */
  _takeMediaAsBackground(e, data) {
    if (data?.type !== "media" || !data.url || !this._nearFrameEdge(e)) return false;

    const index = this._currentSegmentIndex();
    if (index === -1) return false;

    this._imageDropHandler?.cancelDrag();
    this._editor.replaceSegmentMedia({ url: data.url, id: data.id, media_type: data.media_type }, { index, dynamic: data.dynamic });
    return true;
  }

  /** Une image du canvas glissée dans la bande du fond : le fond de la séquence, comme un média
   *  de la toolbox (le calque part, la lib le retire) ; une image dynamique y porte son slot. */
  _takeCanvasImageAsBackground(source) {
    const image = canvasImageSource(source);
    const index = this._currentSegmentIndex();
    if (!image || index === -1) return false;

    const binding = source.get("bindings")?.["image.src"];
    const dynamic = binding && binding.resolved !== true ? { field: "src.url", expr: binding.expr, scope: binding.scope } : undefined;
    this._editor.replaceSegmentMedia({ url: image.url, id: image.imageMeta.content_medium_id, media_type: "image" },
                                     { index, dynamic });
    return true;
  }

  /** La bande du fond (lib editor_stage) ; une séquence vide (à fournir) prend tout le cadre. */
  _nearFrameEdge(e) {
    if (!this._fabricEditor) return false;
    return nearFrameEdge(this._fabricEditor, this.wrapTarget, e, { whole: this._pageEmptyShown });
  }

  _toggleEdgeCue(on) {
    if (this.hasFrameCueTarget) this.frameCueTarget.classList.toggle("hidden", !on);
  }

  _currentSegmentIndex() {
    const { composition, playTime } = this._editor;
    return (composition.segments || []).indexOf(segmentAt(composition, playTime));
  }

  // ==================== Toolbox ====================

  /** Un item de la toolbox sur le canvas (l'orchestrateur a déjà routé les cibles de la
   *  timeline) ; un média vient avec sa cible. */
  placeToolboxItem(item, placement) {
    if (this._fabricEditor) placeToolboxItem(this._toolboxHost, item, placement);
  }

  /** Où irait une image sur le canvas : l'habillage sélectionné s'il en prend une, sinon un
   *  habillage de plus. */
  canvasPlacement() {
    return this._fabricEditor ? canvasPlacement(this._fabricEditor) : null;
  }

  /** Cet éditeur, vu par la toolbox (lib toolbox_dispatch, partagée avec l'éditeur images) :
   *  un calque ajouté reçoit son overlay ; un remplacement garde le layerId de sa cible, son
   *  overlay le retrouve au resync. */
  get _toolboxHost() {
    return {
      editor: this._fabricEditor,
      controls: this._controls,
      dropHandler: this._imageDropHandler,
      onAdded: (obj) => this._registerObject(obj),
      onChanged: () => this._onObjectModified(),
    };
  }

  // ==================== Actions — overlay creation ====================

  addShape(opts = {}) {
    const obj = this._controls?.addShape(opts);
    this._registerObject(obj);
  }

  addText(opts = {}) {
    const obj = this._controls?.addText(opts);
    this._registerObject(obj);
  }

  pickImage() {
    this._editor?.openPicker("fabric:add");
  }

  async addImage(cm) {
    const obj = await this._controls?.addImage(cm);
    if (obj) this._registerObject(obj);
  }

  async replaceImage(cm) {
    await this._controls?.replaceSelectedImage(cm);
  }

  pickReplaceImage() {
    this._editor?.openPicker("fabric:replace");
  }

  // ==================== Actions — events from overlay panel ====================

  /** L'ordre d'empilement du canvas, du bloc le plus haut au plus bas (ses `layerId`) — la
   *  piste Calques range ses lignes comme la slide. */
  stackOrder() {
    if (!this._fabricEditor) return [];
    return this._fabricEditor.canvas.getObjects().map((obj) => obj.get("layerId"))
      .filter((id) => id && id !== BG_LAYER_ID).reverse();
  }

  /** Ouvre une sous-page de la toolbox (« animations »…) pour le calque d'un overlay : le
   *  bloc est sélectionné sur la slide, la page s'ouvre dans la toolbox (et l'ouvre). */
  openOverlayPage(index, page) {
    const overlay = this._editor?.composition.overlays?.[index];
    if (!overlay?._fabricId || !this._controls) return;
    this.selectOverlay(index);
    const url = this._controls.overlayPageUrl(page, overlay._fabricId);
    if (url) window.dispatchEvent(new CustomEvent("toolbox:show", { detail: { key: "overlay", url } }));
  }

  /** Select the canvas object corresponding to overlay at given index. */
  selectOverlay(index) {
    const overlay = this._editor?.composition.overlays?.[index];
    if (!overlay?._fabricId || !this._fabricEditor) return;
    this._fabricEditor.selection.selectByLayerId(overlay._fabricId);
  }

  deselectCanvas() {
    this._fabricEditor?.selection?.clear();
  }

  deselectOutside(e) {
    const wrapper = this.hasWrapTarget && this.wrapTarget.querySelector("[data-fabric='wrapper']");
    if (wrapper?.contains(e.target)) return;
    // TODO: fragile — relies on specific data-attributes to exclude toolbar UI from
    // triggering deselection. Consider a single wrapper element or a CSS class instead.
    if (e.target.closest("[data-role='layerControls'], [data-role='cropBar'], [data-floating-menu-target='menu']")) return;
    this._fabricEditor?.selection?.clear();
  }

  setCornerRadius(e) {
    const radius = e.detail?.value;
    if (typeof radius !== "number") return;
    const obj = this._fabricEditor?.selection?.current;
    if (obj?.setCornerRadius) {
      obj.setCornerRadius(radius);
      this._onObjectModified();
    }
  }

  /** Lie un champ de l'habillage sélectionné (lib fabric_bindings, partagée avec l'éditeur
   *  images) — puis le rituel de CET éditeur : resynchronisation des overlays. v1 vidéo :
   *  le canvas montre l'expression telle quelle (pas d'interpolation d'affichage) ; la
   *  résolution appartient à la divergence chez l'affilié (Composition#resolve_with!), les
   *  bindings voyagent dans les _fabricLayers. */
  bindField(e) {
    if (!bindSelectedField(this._fabricEditor, e.detail)) return;
    // Sérialiser d'abord (withExpressions garde le token), afficher interpolé ensuite.
    this._onObjectModified();
    interpolateBoundFields(this._fabricEditor, this.variablesValue);
  }

  /** Les valeurs du lecteur changent (menu « Variables du document » : on teste) : le canvas
   *  les rejoue, comme l'éditeur images. Le document, lui, ne bouge pas. */
  setVariables(e) {
    this.variablesValue = e.detail?.values || {};
    interpolateBoundFields(this._fabricEditor, this.variablesValue);
  }

  /** Délie un champ : l'expression disparaît, la valeur en place redevient éditable. */
  unbindField(e) {
    if (!unbindSelectedField(this._fabricEditor, e.detail?.field || "text")) return;
    this._onObjectModified();
  }

  /** Le panneau Variables insère une variable : au curseur du texte en édition sur la slide
   *  s'il y en a un (le panneau relit l'expression dans l'événement), sinon rien. */
  insertToken(e) {
    const expr = insertTokenAtCaret(this._fabricEditor, e.detail);
    if (expr === null) return;

    e.detail.expr = expr;
    this._onObjectModified();
  }

  /** Pose le texte de l'habillage sélectionné (sous-page Variables : une expression sans
   *  token n'est pas une liaison, c'est du texte). */
  setText(e) {
    if (!setSelectedText(this._fabricEditor, e.detail?.text)) return;
    this._onObjectModified();
    interpolateBoundFields(this._fabricEditor, this.variablesValue);
  }

  // ==================== Actions — floating toolbar (fc*) ====================

  fcDelete() { this.deleteSelection(); }
  fcToggleOutline() { this._controls?.toggleOutline(); }
  fcSetLockMode({ params }) { this._controls?.setLockMode(params.mode); }
  fcContainerize() { this._controls?.containerize(); }
  fcCrop() { this._controls?.cropSelection(); }
  fcCropBackground() { this._cropSegmentImage(); }
  // La séquence sélectionnée (sa page) : l'orchestrateur fait le geste, la barre suit.
  fcSegmentSpeed({ params }) { this._onSegment((index) => this._editor.setSegmentSpeed(index, params.speed)); }
  fcSegmentRepeat() { this._onSegment((index) => this._editor.toggleSegmentRepeat(index)); }
  fcSegmentDuplicate() { this._onSegment((index) => this._editor.duplicateSegment(index)); }
  fcSegmentDelete() { this._onSegment((index) => this._editor.deleteSegment(index)); }
  fcSegmentRetry() { this._onSegment((index) => this._editor.retrySegment(index)); }
  fcCropReset() { this._controls?.cropReset(); }
  fcCropCancel() { this._controls?.cropCancel(); }
  fcCropDone() { this._controls?.cropDone(); }
  fcBringForward() { this._controls?.bringForward(); }
  fcSendBackward() { this._controls?.sendBackward(); }
  fcGroup() { this._controls?.group(); }
  fcSetArrangement({ params }) { this._controls?.setArrangement(params.arrangement); }
  // Le porteur d'un groupe s'en va : son overlay aussi
  fcUngroup() {
    this._controls?.ungroup();
    if (this._pruneOverlays().length) this._afterOverlayRemoval();
  }
  fcAlign({ params }) { this._controls?.align(params.edge); }
  fcDistribute({ params }) { this._controls?.distribute(params.axis); }

  // ==================== Private ====================

  _onSegment(gesture) {
    if (!this._editor || !this._pageSegment()) return;
    gesture(this._pageIndex);
    this._controls?.refreshPage();
  }

  /** Un geste de souris est en cours sur le canvas (GestureManager). */
  get gestureActive() {
    return Boolean(this._fabricEditor?.gestures.active);
  }

  get _editor() {
    return this.hasCompositionEditorOutlet ? this.compositionEditorOutlet : null;
  }

  _initCanvas() {
    if (!this._editor || !this.hasCanvasTarget) return;
    const composition = this._editor.composition;
    const w = composition.width || DEFAULT_WIDTH;
    const h = composition.height || DEFAULT_HEIGHT;

    this._fabricEditor = new FabricEditor(this.canvasTarget, {
      width: w,
      height: h,
      container: this.wrapTarget,
      fonts: this.fontsValue,
      transparent: true,
      workspace: workspace(this.element),
      // Les variables se lisent en couleur secondaire, sur la slide comme dans le panneau.
      tokenColor: themeColor(this.element, "brand_secondary") ?? undefined,
    });
    // Les raccourcis de l'orchestrateur rejoignent ceux du canvas, dans le gestionnaire de la lib
    this._fabricEditor.shortcuts.register(this._editor?.shortcutEntries?.() ?? []);
    if (this.hasFrameTarget) followFrame(this._fabricEditor, this.frameTarget);
    if (this.hasFrameCueTarget) followFrame(this._fabricEditor, this.frameCueTarget);
    if (this.hasPageOverlayTarget) followFrame(this._fabricEditor, this.pageOverlayTarget);
    // Le cadre est placé : la transition du frame peut se jouer (lib/frame_transitions).
    this.element.dispatchEvent(new CustomEvent("frame:settled", { bubbles: true }));

    this._fabricEditor.onResize(() => this._controls?.repositionActive());

    if (this.hasLayerControlsTarget) {
      // The fabric controller element is the previewArea itself
      const previewArea = this.element;
      this._controls = new FabricControls(this._fabricEditor, this.layerControlsTarget, {
        onModified: () => this._onObjectModified(),
        onSelect: (obj) => this._onSelect(obj),
        // Plusieurs objets : aucun overlay n'est « le » sélectionné
        onSelectMany: () => this._onDeselect(),
        onDeselect: () => this._onDeselect(),
        // La page de la vidéo : la séquence sous la tête de lecture (ou celle choisie dans la
        // timeline). L'orchestrateur la tient pour sélectionnée (kind "segment").
        page: this.hasPageOverlayTarget ? {
          bar: () => this._pageBar(),
          // Sa barre contre la timeline, au bas de l'aperçu : la séquence s'y règle
          dock: "bottom",
          target: () => {
            const seg = this._pageSegment();
            return seg?.id ? { kind: "sequence", id: seg.id } : null;
          },
          onSelect: () => this._onPageSelect(),
          onDeselect: () => {
            if (this._editor?.selection?.kind === "segment") this._editor.deselect();
          },
        } : undefined,
        ...this._fabricEditor.getControlsConfig(previewArea),
      });
      this._controls.attach();
    }

    // Layout — drag-to-layout + panel integration
    this._fabricLayout = new FabricLayout(this._fabricEditor, {
      onChanged: () => this._onObjectModified(),
    });
    this._fabricLayout.attach();

    // Appearance — stroke/fill panel events
    this._fabricAppearance = new FabricAppearance(this._fabricEditor, {
      onChanged: () => this._onObjectModified(),
    });
    this._fabricAppearance.attach();

    // Un texte lié s'édite dans son habillage : à la sortie, resynchro et ré-affichage
    this._detachBoundTextEditing = attachBoundTextEditing(this._fabricEditor, () => {
      this._onObjectModified();
      interpolateBoundFields(this._fabricEditor, this.variablesValue);
    });

    // DropHandler — provides hover-to-replace overlay, add/replace logic,
    // and puppet drag for text/shapes.
    // Un fichier du bureau entre d'abord dans la bibliothèque : l'habillage porte son CM.
    this._imageDropHandler = new DropHandler(this._fabricEditor, {
      hoverDelay: 500,
      getImageUrl: (file) => URL.createObjectURL(file),
      resolveFile: async (file) => {
        const cm = await uploadToLibrary(file, this.uploadEndpointValue);
        return { url: cm.url, imageMeta: mediumMeta(cm) };
      },
      onSuccess: () => this._onObjectModified(),
      onError: (error) => console.error("[composition-editor--fabric] drop error:", error),
      // Une image du canvas glissée au bord du cadre : le fond de la séquence.
      canvasEdge: {
        near: (e) => this._nearFrameEdge(e),
        cue: (on) => this._toggleEdgeCue(on),
        take: (source) => this._takeCanvasImageAsBackground(source),
      },
    });

    this.restoreOverlays();
    // Les formes arrivent après l'éditeur (lib/shape_catalogs) : un clip legacy affiché en
    // rectangle se remonte quand son catalogue est là.
    streamShapeCatalogs(this.shapeCatalogsValue, () => this._remountLegacyClips());
  }

  /** Remonte les habillages si un clip par id seul vient de devenir résoluble (rare, à l'ouverture). */
  _remountLegacyClips() {
    const overlays = this._editor?.composition.overlays || [];
    if (this._fabricEditor && hasResolvableLegacyClip(overlays.map((o) => o._fabricLayers))) this.restoreOverlays();
  }

  /**
   * Supprime la sélection du canvas ET ses overlays — le miroir de _registerObject.
   *
   * Le package retire l'objet du canvas sans rien notifier (aucun callback de suppression),
   * et _syncOverlayLayers ne sait que METTRE À JOUR les overlays des objets présents : sans
   * ce chemin, l'overlay survivait au calque supprimé — barre fantôme dans les tracks,
   * réapparition au rendu Remotion, et résurrection sur le canvas au rechargement (le
   * document corrompu part à l'autosave, restoreOverlays le relit).
   *
   * Appelé aussi par les tracks (suppression depuis la barre d'habillage).
   */
  deleteSelection() {
    if (!this._fabricEditor || !this._editor) return;

    // Le package emporte la descendance d'un conteneur (même règle que le document)
    this._controls?.deleteSelection();
    // On ne déduit rien de la sélection : le package refuse les calques verrouillés en
    // position. Ce sont les objets réellement absents du canvas qui décident — et s'il n'y
    // en a aucun, rien n'a bougé, donc pas de mutation à propager.
    if (this._pruneOverlays().length) this._afterOverlayRemoval();
  }

  /** Supprime l'habillage d'index donné (tracks) : sa sélection canvas, puis le chemin commun. */
  deleteOverlayAt(index) {
    const overlay = this._editor?.composition.overlays?.[index];
    if (!overlay) return;

    // Un overlay fabric se supprime par son objet (le canvas mène) ; un overlay sans objet
    // (import, habillage non-fabric) se retire directement du document.
    if (overlay._fabricId && this._fabricEditor?.selection.selectByLayerId(overlay._fabricId)) {
      this.deleteSelection();
      return;
    }

    this._editor.composition.overlays.splice(index, 1);
    this._afterOverlayRemoval();
  }

  /** Le filet : aucun overlay fabric ne survit à la disparition de son objet. */
  _pruneOverlays() {
    if (!this._fabricEditor || !this._editor) return [];

    const live = this._fabricEditor.canvas.getObjects()
      .map((obj) => obj.get("layerId"))
      .filter(Boolean);

    return pruneOrphanOverlays(this._editor.composition, live);
  }

  // La sélection est un INDEX dans overlays : après un retrait elle désignerait un autre
  // habillage (ou le vide). On la lâche, puis on laisse l'éditeur propager (tracks, autosave,
  // undo) — comme toute mutation du document.
  _afterOverlayRemoval() {
    if (this._editor.selection?.kind === "overlay") this._editor.deselect();
    if (this.hasLayerControlsTarget) this.layerControlsTarget.hidden = true;
    this._editor.mutate();
  }

  /**
   * Register a newly created fabric object as a composition overlay.
   */
  _registerObject(obj) {
    if (!obj) return;
    const editor = this._editor;
    if (!editor) return;

    this._createOverlay(obj);
    this._syncOverlayLayers();

    this.setEditable(true);
    editor.mutate();
    editor.toast("Bloc ajouté");
  }

  /**
   * L'overlay d'un objet : sa fenêtre est celle de son conteneur s'il naît dedans (un
   * enfant n'existe pas hors de son parent), celle de ses enfants s'il naît autour d'eux
   * (un groupe), sinon celle de la séquence sous la tête de lecture.
   */
  _createOverlay(obj) {
    const composition = this._editor.composition;
    const fabricId = obj.get("layerId");
    if (!composition.overlays) composition.overlays = [];

    const parentId = obj.get("layout")?.child?.parentId;
    const parent = parentId ? composition.overlays.find((o) => o._fabricId === parentId) : null;
    const scene = segmentAt(composition, this._editor.playTime);
    const fallback = totalOutputDuration(composition) || 5;
    let start = parent ? parent.start : scene?.id ? segmentEdgeTimeById(composition, scene.id, "start") ?? 0 : 0;
    let end = parent ? parent.end : scene?.id ? segmentEdgeTimeById(composition, scene.id, "end") ?? fallback : fallback;

    // Un groupe qui naît autour d'objets déjà là couvre leurs fenêtres : un enfant ne vit
    // que dans celle de son parent
    const children = composition.overlays.filter((o) =>
      this._fabricEditor.layers.findById(o._fabricId)?.get("layout")?.child?.parentId === fabricId);
    if (!parent && children.length) {
      start = Math.min(...children.map((o) => o.start));
      end = Math.max(...children.map((o) => o.end));
    }

    composition.overlays.push({
      _fabricId: fabricId,
      _fabricLayers: [this._serializeObject(obj)],
      type: "html",
      content: "",
      start,
      end,
      position: this._fabricEditor.getObjectBounds(obj),
      animations: [],
      color: "#8B5CF6",
    });
  }

  /**
   * Called when a fabric object is selected on the canvas.
   * Syncs the overlay selection in the timeline and opens the overlay panel.
   */
  _onSelect(obj) {
    const fabricId = obj.get("layerId");
    if (!fabricId) return;
    const overlays = this._editor?.composition.overlays || [];
    const idx = overlays.findIndex((o) => o._fabricId === fabricId);
    if (idx !== -1) {
      this._editor.select("overlay", idx);
      window.dispatchEvent(new CustomEvent("toolbox:show", {
        detail: { key: "overlay" },
      }));
    }
  }

  /**
   * Called when fabric selection is cleared.
   */
  _onDeselect() {
    if (this._editor?._playing) return;
    if (this._editor?.selection?.kind === "overlay") {
      this._editor.deselect();
    }
    // La sous-page d'un calque revient à la home ; la bibliothèque ou un onglet restent
    if (this._controls?.layerPageOpen()) {
      window.dispatchEvent(new CustomEvent("toolbox:show", { detail: { key: "home" } }));
    }
    // Plusieurs objets sélectionnés ne passent pas par l'orchestrateur : la cible change
    // quand même (plus de calque à remplacer).
    this._editor?.publishPlacement();
  }

  /** Toute mutation d'un objet du canvas : les overlays se resynchronisent (chaque objet
   *  garde son calque, cf. _syncOverlayLayers), puis l'éditeur fait son rituel. */
  _onObjectModified() {
    const editor = this._editor;
    if (!editor || this._backgroundFrame) return;
    this._syncOverlayLayers();
    editor.onFabricModified();
  }

  /** Le document vient d'être sauvegardé : la sous-page ouverte de la toolbox le suit. */
  reloadOverlayPanel() {
    this._controls?.reloadOverlayPanel();
  }

  /** Un objet du canvas tel que l'overlay le stocke : les propriétés de l'éditeur (lib
   *  LAYER_PROPS), et un champ lié EN ATTENTE avec son token, quelle que soit la valeur
   *  affichée (withExpressions, même contrat que l'export de l'éditeur images). */
  _serializeObject(obj) {
    return withExpressions([{ ...obj.toObject([...LAYER_PROPS]), visible: true }])[0];
  }

  /**
   * La réconciliation canvas → document : chaque objet du canvas a son overlay, qui porte
   * SON calque et lui seul. Un objet sans overlay en reçoit un (stock dont l'overlay enfant
   * a été nettoyé, objet créé par un chemin non câblé), un overlay sans objet part (filet).
   *
   * Un enfant de conteneur garde donc son calque chez lui — le rendu (creatorstudio,
   * FabricTree) reconstruit l'arbre pour runLayout et compose l'enfant dans son parent. Le
   * stock historique, où les calques des enfants étaient recopiés chez le conteneur et
   * vidés chez l'enfant, revient à cette forme ici, à la première synchro.
   *
   * Doit tourner après TOUT changement du mapping canvas ↔ overlays (modification, création).
   */
  _syncOverlayLayers() {
    const overlays = this._editor?.composition.overlays || [];

    this._fabricEditor.canvas.getObjects().forEach((obj) => {
      const id = obj.get("layerId");
      // Le cadre `bg` du recadrage (_cropSegmentImage) n'est pas un habillage
      if (!id || id === BG_LAYER_ID) return;
      let overlay = overlays.find((o) => o._fabricId === id);
      if (!overlay) {
        this._createOverlay(obj);
        overlay = overlays[overlays.length - 1];
      }
      overlay.position = this._fabricEditor.getObjectBounds(obj);
      overlay._fabricLayers = [this._serializeObject(obj)];
    });

    this._pruneOverlays();
  }
}

